<script setup lang="ts">
  import { ref, watch } from 'vue';
  import ArrowRightCircleIcon from '@/shared/assets/icons/ArrowRightCircleIcon.svg';
  import ArrowDown from '@/shared/assets/icons/ArrowDown.svg';
  import { IconImage, SelectInput } from '@/shared/ui';
  import RuleCardFooter from './RuleCardFooter.vue';
  import RuleThenBlockCard from './RuleThenBlockCard.vue';
  import type { ConditionalRuleAction } from '../../model/form-rules.store';

  interface SelectOption {
    id: string;
    name: string;
  }

  interface RuleTargetView {
    readonly id: string;
    readonly type: 'field' | 'table';
    readonly label: string;
    readonly fieldIds: readonly string[];
  }

  interface RuleThenBlockView {
    readonly id: string;
    readonly action: ConditionalRuleAction | '';
    readonly targets: readonly RuleTargetView[];
  }

  interface RuleView {
    readonly id: string;
    readonly name: string;
    readonly triggerOption: string;
    readonly thenBlocks: readonly RuleThenBlockView[];
  }

  interface ThenActionPayload {
    block: RuleThenBlockView;
    value: string | number;
  }

  interface ThenIndexedPayload {
    block: RuleThenBlockView;
    index: number;
  }

  const props = defineProps<{
    rule: RuleView;
    expanded: boolean;
    triggerOptions: SelectOption[];
    actionOptions: SelectOption[];
    activeThenBlockId?: string | null;
    editingLocked?: boolean;
    readonly?: boolean;
    nameError?: boolean;
    unsavedError?: boolean;
    resetVersion?: number;
    isFocused?: boolean;
    focusVersion?: number;
  }>();

  const emit = defineEmits<{
    (event: 'toggle', ruleId: string): void;
    (event: 'update-rule-name', value: string): void;
    (event: 'update-trigger-option', value: string | number): void;
    (event: 'update-then-action', payload: ThenActionPayload): void;
    (event: 'add-item', block: RuleThenBlockView): void;
    (event: 'delete-then', payload: ThenIndexedPayload): void;
    (event: 'confirm-then'): void;
    (event: 'remove-target', payload: { block: RuleThenBlockView; targetId: string }): void;
    (event: 'add-condition'): void;
    (event: 'delete-rule'): void;
  }>();

  const ruleNameInput = ref(props.rule.name);
  const lastValidRuleName = ref(props.rule.name);
  const ruleNameInputResetKey = ref(0);

  watch(
    () => props.rule.name,
    (name) => {
      ruleNameInput.value = name;
      lastValidRuleName.value = name;
    }
  );

  watch(
    () => props.resetVersion,
    () => {
      ruleNameInput.value = props.rule.name;
      lastValidRuleName.value = props.rule.name;
      ruleNameInputResetKey.value += 1;
    }
  );

  function isThenBlockActive(blockId: string) {
    return props.activeThenBlockId === blockId;
  }

  function isThenBlockBlocked(blockId: string) {
    return !!props.activeThenBlockId && !isThenBlockActive(blockId);
  }

  function isItemSelectionLocked() {
    return !!props.activeThenBlockId;
  }

  function handleRuleNameInput(event: Event) {
    if (props.readonly || props.editingLocked || isItemSelectionLocked()) return;
    ruleNameInput.value = (event.target as HTMLInputElement).value;
  }

  function handleRuleNameChange() {
    if (props.readonly || props.editingLocked || isItemSelectionLocked()) return;

    const trimmed = ruleNameInput.value.trim();
    if (!trimmed) {
      emit('update-rule-name', trimmed);
      ruleNameInput.value = lastValidRuleName.value;
      return;
    }

    ruleNameInput.value = trimmed;
    lastValidRuleName.value = trimmed;
    emit('update-rule-name', trimmed);
  }
</script>

<template>
  <div class="flex shrink-0 flex-col gap-0.5">
    <article
      :key="props.isFocused ? props.focusVersion : 0"
      class="flex flex-col rounded-lg border bg-white-02 p-4"
      :class="[
        nameError || unsavedError ? 'border-[var(--theme-secondary-ink)]' : 'border-gray-02',
        {
          'animate-[rule-focus-border_800ms_ease-out]':
            props.isFocused && !(nameError || unsavedError),
          'animate-[rule-focus-error-border_800ms_ease-out]':
            props.isFocused && (nameError || unsavedError),
        },
      ]"
      :aria-invalid="nameError || unsavedError || undefined"
    >
      <div class="flex w-full items-center justify-between gap-4">
        <div class="flex min-w-0 flex-1 items-center gap-2 text-left">
          <span
            class="flex shrink-0 items-center justify-center rounded bg-[var(--theme-secondary)] text-white"
          >
            <IconImage :src="ArrowRightCircleIcon" :alt="rule.name" class="[&_path]:stroke-white" />
          </span>
          <input
            :key="ruleNameInputResetKey"
            :value="ruleNameInput"
            type="text"
            aria-label="Nome da regra"
            :disabled="readonly || editingLocked || isItemSelectionLocked()"
            class="min-w-0 flex-1 bg-transparent text-sm font-medium text-gray-07 outline-none disabled:cursor-default"
            @click.stop
            @input="handleRuleNameInput"
            @change="handleRuleNameChange"
          />
        </div>
        <button
          type="button"
          class="flex size-8 shrink-0 items-center justify-center rounded-lg text-gray-05 disabled:cursor-not-allowed disabled:opacity-50"
          :aria-expanded="expanded"
          :aria-label="`${expanded ? 'Recolher' : 'Expandir'} ${rule.name}`"
          :disabled="editingLocked"
          @click="emit('toggle', rule.id)"
        >
          <IconImage
            :src="ArrowDown"
            alt="Expandir regra"
            class="transition-transform"
            :class="{ 'rotate-180': expanded }"
          />
        </button>
      </div>

      <div class="mt-2 border-t border-gray-03" />

      <div
        class="mt-4 flex flex-col gap-4"
        :style="expanded ? undefined : { display: 'none' }"
        data-testid="saved-rule-details"
      >
        <div class="flex flex-col gap-2 rounded-lg border border-gray-02 bg-white-02 p-4">
          <label class="text-base font-medium text-gray-07">Quando selecionar:</label>
          <SelectInput
            :model-value="rule.triggerOption"
            :options="triggerOptions"
            :disabled="readonly || isItemSelectionLocked()"
            label="Escolha a opção"
            button-class="h-[44px] bg-white-01 border border-gray-02 text-sm text-gray-07 shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]"
            max-height="220px"
            @update:model-value="emit('update-trigger-option', $event)"
          />
        </div>

        <RuleThenBlockCard
          v-for="(thenBlock, thenIndex) in rule.thenBlocks"
          :key="thenBlock.id"
          :action="thenBlock.action"
          :targets="thenBlock.targets"
          :action-options="actionOptions"
          :action-disabled="readonly || isItemSelectionLocked()"
          :delete-disabled="
            readonly ||
            (thenBlock.targets.length === 0 && thenIndex === 0) ||
            isItemSelectionLocked() ||
            isThenBlockBlocked(thenBlock.id)
          "
          :add-disabled="readonly || !thenBlock.action || isItemSelectionLocked()"
          :confirm-disabled="readonly || !isThenBlockActive(thenBlock.id)"
          :target-removal-disabled="readonly || !isThenBlockActive(thenBlock.id)"
          :selection-active="isThenBlockActive(thenBlock.id)"
          @add-item="emit('add-item', thenBlock)"
          @delete="emit('delete-then', { block: thenBlock, index: thenIndex })"
          @confirm="emit('confirm-then')"
          @update-action="emit('update-then-action', { block: thenBlock, value: $event })"
          @remove-target="emit('remove-target', { block: thenBlock, targetId: $event })"
        />

        <RuleCardFooter
          :add-disabled="readonly || rule.thenBlocks.length >= 3 || editingLocked"
          :delete-disabled="readonly || editingLocked"
          @add-condition="emit('add-condition')"
          @delete-rule="emit('delete-rule')"
        />
      </div>
    </article>
    <p
      v-if="nameError"
      class="text-xs leading-[18px] text-[var(--theme-secondary-ink)]"
      data-testid="duplicate-rule-name-error"
    >
      Já existe uma regra com este nome
    </p>
    <p
      v-if="unsavedError"
      class="text-xs leading-[18px] text-[var(--theme-secondary-ink)]"
      data-testid="saved-rule-unsaved-error"
    >
      Regra não salva
    </p>
  </div>
</template>

<style scoped>
  @keyframes rule-focus-border {
    0%,
    65% {
      border-color: var(--theme-secondary-ink);
    }

    100% {
      border-color: var(--color-gray-02);
    }
  }

  @keyframes rule-focus-error-border {
    0%,
    65%,
    100% {
      border-color: var(--theme-secondary-ink);
    }
  }
</style>
